import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Workflow,
  Users,
  ImageIcon,
  Cloud,
  Share2,
  CheckCircle2,
  ArrowRight,
  Calendar,
  Camera,
  PackageCheck,
  Send,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PhoneInput, isValidPhoneNumber } from "@/components/ui/phone-input";
import { submitDemo } from "../lib/api";
import { pixtrayaProduct, pixtrayaLogo } from "@/assets/images";

const PIXTRAYA_HERO = pixtrayaProduct;
const PIXTRAYA_LOGO = pixtrayaLogo;

const FEATURES = [
  { icon: Workflow, title: "Workflow Automation", desc: "Pipelines that handle culling, tagging, editing handoffs, and delivery — so your team can focus on craft." },
  { icon: Users, title: "Client Management", desc: "A unified CRM for every enquiry, shoot, approval, and follow-up, with timeline and role-based access." },
  { icon: ImageIcon, title: "Image Tracking & Delivery", desc: "Track every frame through the pipeline with real-time status, versioning, and secure deliveries." },
  { icon: Share2, title: "Team Collaboration", desc: "Real-time collaboration for shooters, editors, retouchers, and producers — with clear ownership." },
  { icon: Cloud, title: "Cloud-Based Access", desc: "Global CDN-backed storage and delivery. Access anywhere — studio, set, or on the road." },
  { icon: PackageCheck, title: "Delivery & Invoicing", desc: "Branded client galleries, e-signed approvals, and integrated invoicing with GST-ready exports." },
];

const STEPS = [
  { icon: Calendar, t: "Book", d: "Capture enquiries and convert them into structured shoot projects." },
  { icon: Camera, t: "Shoot", d: "Plan, assign, and execute shoots with complete briefs and logistics." },
  { icon: ImageIcon, t: "Process", d: "Automate culling, edits, reviews, and revisions in one pipeline." },
  { icon: Send, t: "Deliver", d: "Branded galleries, client approvals, and invoicing — all in one place." },
];

const FAQ = [
  { q: "Is PixTraya built specifically for photography studios?", a: "Yes. PixTraya is a vertical SaaS product engineered for the realities of professional photography — covering everything from studio operations to delivery and post-sales." },
  { q: "Can PixTraya integrate with my existing tools?", a: "PixTraya supports integrations with common storage, accounting, and communication tools. Our team also builds custom integrations as part of enterprise plans." },
  { q: "Is my data secure on PixTraya?", a: "Absolutely. We use end-to-end encryption, role-based access, least-privilege principles, and full audit logs across all plans." },
  { q: "Do you offer onboarding and support?", a: "Yes. Every PixTraya subscription includes guided onboarding, workflow mapping, and ongoing customer success support." },
];

const Products = () => {
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (location.hash === "#demo") {
      const el = document.getElementById("demo");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in name and email.");
      return;
    }
    setLoading(true);
    setSuccess(null);
    try {
      const saved = await submitDemo(form);
      toast.success("Demo requested! Our team will reach out within one business day.");
      setSuccess(saved);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-products">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="container-section grid items-center gap-12 py-16 md:py-24 lg:grid-cols-2">
          <div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm" data-testid="pixtraya-logo-wrap">
              <img src={PIXTRAYA_LOGO} alt="PixTraya logo" className="h-12 w-12 object-contain" />
              <div className="pr-2">
                <p className="font-[Outfit] text-xl font-bold tracking-tight text-[#5B21B6]">PixTraya</p>
                <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-slate-500">by Ortelly Systems</p>
              </div>
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C4B5]">Flagship Product</span>
            <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              <span className="brand-gradient-text">PixTraya</span> — the end-to-end photography workflow platform.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
              PixTraya helps studios and professional photographers manage their entire process — booking, shoot planning, image processing, client review, delivery, and client management — from a single beautifully designed workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="products-cta-demo" className="rounded-md bg-[#0046B5] text-white hover:bg-[#003A99]">
                <a href="#demo">Request Demo <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
              <Button asChild variant="outline" data-testid="products-cta-contact" className="rounded-md border-slate-200 hover:text-[#0046B5]">
                <a href="#features">Explore Features</a>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 -z-10 rounded-2xl bg-gradient-to-br from-[#5B21B6]/20 to-[#00C4B5]/20 blur-2xl" />
            <div className="flex aspect-square w-full max-w-md items-center justify-center rounded-lg border border-slate-200 bg-white p-12 shadow-xl">
              <img src={PIXTRAYA_LOGO} alt="PixTraya — photography workflow platform" className="h-full w-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-section py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">How PixTraya Works</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">One connected platform. Four simple stages.</h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.t} className="relative rounded-md border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="font-[Outfit] text-3xl font-bold text-slate-200">0{i + 1}</span>
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="container-section py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Key Features</span>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Everything your studio needs — all in one place.</h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g,'-')}`} className="group rounded-md border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#0046B5]/40 hover:shadow-md">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5] transition-colors group-hover:bg-[#0046B5] group-hover:text-white">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Demo form */}
      <section id="demo" className="border-y border-slate-200 bg-slate-50">
        <div className="container-section grid items-start gap-12 py-16 md:py-24 lg:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Request a Demo</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              See PixTraya in action on your workflow.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Tell us a little about your studio. We'll reach out within one business day to schedule a guided walkthrough tailored to your workflow.
            </p>
            <ul className="mt-6 space-y-3">
              {["Personalised 30-minute walkthrough", "Workflow mapping session", "Pricing tailored to your studio size"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#00C4B5]" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            {success && (
              <Alert
                data-testid="demo-success-card"
                className="mb-6 border-[#00C4B5]/40 bg-[#00C4B5]/10 text-slate-900"
              >
                <CheckCircle2 className="h-5 w-5 text-[#00C4B5]" />
                <AlertTitle className="font-[Outfit] text-base font-semibold text-slate-900">
                  Demo request received — saved to our database.
                </AlertTitle>
                <AlertDescription>
                  <p className="mt-1 text-slate-700">
                    Thanks <strong>{success.name}</strong>. We've securely stored your request and our team will email <strong>{success.email}</strong> within one business day.
                  </p>
                  <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs text-slate-600">
                    <dt className="font-semibold">Reference</dt>
                    <dd className="font-mono">{success.id}</dd>
                    <dt className="font-semibold">Received</dt>
                    <dd>{new Date(success.created_at).toLocaleString()}</dd>
                    <dt className="font-semibold">Email notified</dt>
                    <dd>{success.email_sent ? "Yes" : "Stored in DB (email delivery pending)"}</dd>
                  </dl>
                  <button
                    type="button"
                    onClick={() => setSuccess(null)}
                    data-testid="demo-success-dismiss"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0046B5] hover:underline"
                  >
                    <X className="h-3 w-3" /> Dismiss
                  </button>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="d-name">Full name *</Label>
                  <Input id="d-name" name="name" value={form.name} onChange={handleChange} data-testid="demo-input-name" required className="mt-1.5" placeholder="Jane Photographer" />
                </div>
                <div>
                  <Label htmlFor="d-email">Work email *</Label>
                  <Input id="d-email" type="email" name="email" value={form.email} onChange={handleChange} data-testid="demo-input-email" required className="mt-1.5" placeholder="you@studio.com" />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="d-company">Studio / Company</Label>
                  <Input id="d-company" name="company" value={form.company} onChange={handleChange} data-testid="demo-input-company" className="mt-1.5" placeholder="Lumen Studio" />
                </div>
                <div>
                  <Label htmlFor="d-phone">Phone</Label>
                  <PhoneInput
                    id="d-phone"
                    name="phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v || "" })}
                    defaultCountry="IN"
                    placeholder="Enter phone number"
                    data-testid="demo-input-phone"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="d-message">Tell us about your workflow</Label>
                <Textarea id="d-message" name="message" value={form.message} onChange={handleChange} data-testid="demo-input-message" className="mt-1.5" rows={4} placeholder="We run a wedding studio with 4 photographers and..." />
              </div>
              <Button type="submit" disabled={loading} data-testid="demo-submit-btn" className="h-12 w-full rounded-md bg-[#0046B5] text-white hover:bg-[#003A99]">
                {loading ? "Sending…" : "Request Demo"}
              </Button>
            </form>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-section py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">FAQ</span>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Frequently asked questions</h2>
        </div>
        <div className="mt-10 max-w-3xl">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base font-medium text-slate-900 hover:text-[#0046B5]">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Products;
